/**
 * `IdentityEvidencePanel` — per-agent evidence footprint. New in
 * ADR-0159 §3 (the teaching layer merchants pay to see).
 *
 * Renders the `AGENT_EVIDENCE[roster.id]` list. Grade-A facts render
 * bare; grade-B facts render with the `frame` prefix ("Documented by
 * vendor:", "Observed:", "Reported:"). The source citation is a mono
 * caption underneath each claim.
 *
 * Empty evidence (custom / unverified) renders a small placeholder
 * so the panel keeps its shape.
 */

import { BookOpen } from 'lucide-react';

import { Card } from '../../../components/atoms/index.js';
import { useC } from '../../../lib/theme.js';
import { AGENT_EVIDENCE, type EvidenceFact } from '../evidence.js';
import { robotsNoteFor } from '../journeys/discovery.js';
import type { AgentDef } from '../roster.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

function framePrefix(frame: EvidenceFact['frame']): string {
  switch (frame) {
    case 'documented-by-vendor':
      return 'Documented by vendor:';
    case 'observed':
      return 'Observed:';
    case 'reported':
      return 'Reported:';
    default:
      return '';
  }
}

export interface IdentityEvidencePanelProps {
  agent: AgentDef;
}

export function IdentityEvidencePanel({ agent }: IdentityEvidencePanelProps): JSX.Element | null {
  const C = useC();
  const facts = AGENT_EVIDENCE[agent.id] ?? [];

  // No evidence → render nothing (a panel explaining its own emptiness
  // is clutter; 2026-07-06 consolidation).
  if (facts.length === 0) return null;

  return (
    <Card className="p-5 mt-5">
      <div className="flex items-center gap-2 mb-4 pb-3 flex-wrap" style={{ borderBottom: `1px solid ${C.border}` }}>
        <BookOpen size={14} color={C.limeFg} />
        <span style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 2 }}>
          IDENTITY EVIDENCE · {agent.name.toUpperCase()}
        </span>
        <span className="flex-1" />
        {/* ADR-0163 §5 — the crawler-etiquette fact renders ONCE here
            instead of on every discovery-phase narration. */}
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            color: agent.honorsRobots ? C.muted : C.amberFg,
            letterSpacing: 1,
            border: `1px solid ${C.borderSoft}`,
            borderRadius: 2,
            padding: '2px 6px',
          }}
        >
          robots.txt · {robotsNoteFor(agent)}
        </span>
      </div>

      {(
        <div className="space-y-3">
          {facts.map((fact, i) => {
            const prefix = fact.grade === 'B' ? framePrefix(fact.frame) : '';
            return (
              <div key={i} className="flex gap-3">
                <div
                  className="flex-shrink-0"
                  style={{
                    width: 3,
                    background: fact.grade === 'A' ? C.limeFg : C.amberFg,
                    borderRadius: 1,
                  }}
                />
                <div className="flex-1 min-w-0">
                  <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, lineHeight: 1.55 }}>
                    {prefix.length > 0 ? (
                      <>
                        <span style={{ color: C.amberFg, fontWeight: 500 }}>{prefix} </span>
                        {fact.claim}
                      </>
                    ) : (
                      fact.claim
                    )}
                  </div>
                  <div
                    className="mt-1"
                    style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 0.3 }}
                  >
                    {fact.grade === 'A' ? 'Grade A · ' : 'Grade B · '}
                    {fact.source}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
